import React from "react";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import PropTypes from "prop-types";
import clsx from "clsx";
import { lighten, makeStyles } from "@material-ui/core/styles";
import moment from "moment";

import Autocomplete from "@material-ui/lab/Autocomplete";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell, { SortDirection } from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import { Formatter, isNullOrEmpty, isNullOrUndefined, debounce } from "./Util";
import DateFnsUtils from "@date-io/date-fns";
import { Select, FormControl, InputLabel, MenuItem, ListItemText, Input, Button, TextField } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
	},
	paper: {
		width: "100%",
		marginBottom: theme.spacing(2),
	},
	table: {
		minWidth: 750,
	},
	tableWrapper: {
		overflowX: "auto",
	},
	visuallyHidden: {
		border: 0,
		clip: "rect(0 0 0 0)",
		height: 1,
		margin: -1,
		overflow: "hidden",
		padding: 0,
		position: "absolute",
		top: 20,
		width: 1,
	},
	thead: {},
	toolbarRoot: {
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(1),
		borderTopLeftRadius: "5px",
		borderTopRightRadius: "5px",
	},
	highlight:
		theme.palette.type === "light"
			? {
					color: theme.palette.secondary.main,
					backgroundColor: lighten(theme.palette.secondary.light, 0.85),
			  }
			: {
					color: theme.palette.text.primary,
					backgroundColor: theme.palette.secondary.dark,
			  },
	title: {
		flex: "1 1 100%",
	},
}));
function desc(a: any, b: any, orderBy: string) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

function stableSort(array: Array<any>, cmp: any) {
	const stabilizedThis = array.map((el: any, index: number) => [el, index]);
	stabilizedThis.sort((a: any, b: any) => {
		const order = cmp(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

function getSorting(order: string, orderBy: any) {
	return order === "desc" ? (a: any, b: any) => desc(a, b, orderBy) : (a: any, b: any) => -desc(a, b, orderBy);
}

interface TheadProps {
	classes: any;
	order: SortDirection;
	orderBy: string;
	onRequestSort: Function;
	cells: Array<any>;
}
function EnhancedTableHead(props: TheadProps) {
	const classList = useStyles();
	const { classes, order, orderBy, onRequestSort } = props;
	const createSortHandler = (property: any) => (event: any) => {
		onRequestSort(event, property);
	};

	return (
		<TableHead className={classList.thead}>
			<TableRow>
				{props.cells
					.filter((cell) => {
						return cell.visible;
					})
					.map((headCell) => (
						<TableCell key={headCell.columnName} align={headCell.numeric ? "right" : "left"} padding={headCell.disablePadding ? "none" : "default"} sortDirection={orderBy === headCell.columnName ? order : "desc"}>
							<TableSortLabel active={orderBy === headCell.columnName} direction={order == "desc" ? "desc" : "asc"} onClick={createSortHandler(headCell.columnName)}>
								<>
									{headCell.header}
									{orderBy === headCell.columnName ? <span className={classes.visuallyHidden}>{order === "desc" ? "sorted descending" : "sorted ascending"}</span> : null}
								</>
							</TableSortLabel>
						</TableCell>
					))}
			</TableRow>
		</TableHead>
	);
}

EnhancedTableHead.propTypes = {
	classes: PropTypes.object.isRequired,
	onRequestSort: PropTypes.func.isRequired,
	order: PropTypes.oneOf(["asc", "desc"]).isRequired,
	orderBy: PropTypes.string.isRequired,
	rowCount: PropTypes.number.isRequired,
};

const TicketTypes = ["Maintenance", "Repair", "Call Back", "Violation", "Open Order", "Sale", "Nuisance"];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

interface ToolbarProps {
	dateFilter: Date;
	ShowBuildingsFilter: boolean;
	Title: string;
	NewButtonText: string;
	variant: "dense" | "regular" | undefined;
	ShowNewButton: boolean;
	NewOnClick: any;
	showDateFilter: boolean;
	onChangeDateFilter: any;
	showTicketTypeFilter: boolean;
	ticketTypeFilter: Array<string>;
	onChangeTicketTypeFilter: Function;
	FilterBuildingName: Function;
	ShowGlobalFilter: boolean;
	FilterCallback: Function;
}
const EnhancedTableToolbar = (props: ToolbarProps) => {
	const classes: any = useStyles();

	var dt = props.dateFilter;

	var [myBuildings, setMyBuildings] = React.useState([]);
	var [hasQueriedBuildings, setHasQueried] = React.useState(false);

	if (props.ShowBuildingsFilter && myBuildings.length === 0 && !hasQueriedBuildings) {
		setHasQueried(true);
		fetch("Building/GetClientPortalBuildings", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: "{}",
		})
			.then((dt) => dt.json())
			.then((d) => setMyBuildings(d));
	}
	if (new Date(dt).getTime() === new Date("1/1/1900").getTime()) {
		dt = null;
	}

	return (
		<Toolbar className={clsx(classes.toolbarRoot)} variant={props.variant}>
			<Typography className={classes.title} variant="h6" id="tableTitle" display="inline">
				{props.Title || "Tickets"}
			</Typography>

			{props.ShowNewButton && (
				<Button onClick={props.NewOnClick} style={{ whiteSpace: "nowrap", flexShrink: 0, marginRight: "8px" }} color="primary" variant="contained">
					{props.NewButtonText}
				</Button>
			)}

			{props.showDateFilter && (
				<MuiPickersUtilsProvider utils={DateFnsUtils}>
					<KeyboardDatePicker autoOk variant="inline" label="Ticket Date" format="MM/dd/yyyy" value={dt} InputAdornmentProps={{ position: "start" }} onChange={(date) => props.onChangeDateFilter(date)} />
				</MuiPickersUtilsProvider>
			)}

			{props.showTicketTypeFilter && (
				<FormControl className={classes.formControl} style={{ minWidth: "250px", marginLeft: "15px" }}>
					<InputLabel id="ticket-type-filter-label">Ticket Type</InputLabel>
					<Select
						labelId="ticket-type-filter-label"
						id="ticket-type-filter"
						multiple
						value={props.ticketTypeFilter}
						onChange={(event: any, x: any) => {
							var newValue = event.target.value;
							if (x.props.value === "all") {
								newValue = x.props.children[0].props.checked ? [] : TicketTypes;
							}
							props.onChangeTicketTypeFilter(newValue);
						}}
						input={<Input />}
						renderValue={(selected: any) => selected.join(", ")}
						MenuProps={MenuProps}>
						<MenuItem key="all" value="all">
							<Checkbox checked={props.ticketTypeFilter.toString().split(",").length === TicketTypes.length} />
							<ListItemText primary={"All"} />
						</MenuItem>
						{TicketTypes.map((name) => (
							<MenuItem key={name} value={name}>
								<Checkbox checked={props.ticketTypeFilter.indexOf(name) > -1} />
								<ListItemText primary={name} />
							</MenuItem>
						))}
					</Select>
				</FormControl>
			)}

			{props.ShowBuildingsFilter && (
				<Autocomplete
					options={myBuildings}
					getOptionLabel={(option: any) => option.building_name}
					style={{ width: 400 }}
					selectOnFocus={true}
					onChange={(event: any, item: any) => {
						props.FilterBuildingName(item ? item.building_name : "");
					}}
					renderInput={(params: any) => <TextField {...params} label="Select a building" size="small" variant="outlined"></TextField>}></Autocomplete>
			)}

			{props.ShowGlobalFilter && props.FilterCallback && (
				<TextField
					label="Search Table"
					style={{ marginLeft: "15px", width: "350px" }}
					InputLabelProps={{ shrink: true }}
					onChange={(event) => {
						props.FilterCallback(event.currentTarget.value);
					}}></TextField>
			)}
		</Toolbar>
	);
};

interface GridProps {
	Title: string | undefined;
	Configuration: Array<any>;
	DataSet: Array<any>;
	Settings: any;
	ShowTicketTypeFilter: boolean | undefined;
	OnRowClick: Function | undefined;
	SortAscending?: boolean | undefined;
	Modifiable?: boolean | undefined;
	UseDbFilterAndSort?: boolean | undefined;
	Refresh?: Function | undefined;
}

const filters = {
	dateFilter: null as any,
	ticketTypeFilter: null as any,
	globalFilter: null as any,
	pageNumber: 0 as any,
	rowsPerPage: 10 as any,
	orderBy: [{ ColumnName: "ticket_number", IsDescending: true }] as any,
};
function DynamicGrid(props: GridProps) {
	const classes = useStyles();
	const [order, setOrder] = React.useState(props.SortAscending ? "asc" : "desc");
	const [orderBy, setOrderBy] = React.useState(props.Settings.SortColumn);
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);
	const [ticketTypeFilter, setTicketTypeFilter] = React.useState(TicketTypes);
	const [dataSet, setDataSet] = React.useState(props.DataSet);

	const rows = props.DataSet;
	const settings = props.Settings;
	const [dateFilter, setDateFilter] = React.useState(new Date("1/1/1900"));
	const [globalFilter, setGlobalFilter] = React.useState("");

	const [buildingFilter, setBuildingFilter] = React.useState("");
	const handleRequestSort = (event: any, property: any) => {
		const isDesc = orderBy === property && order === "desc";
		setOrder(isDesc ? "asc" : "desc");
		setOrderBy(property);
		const o = [{ ColumnName: orderBy, IsDescending: !isDesc }];
		filters.orderBy = o;
		callRefresh();
	};

	const callRefresh = debounce(() => {
		if (props.UseDbFilterAndSort) {
			props.Refresh(filters.pageNumber, filters.rowsPerPage, filters.dateFilter, filters.ticketTypeFilter, filters.globalFilter, filters.orderBy);
		}
	});

	const changeGlobalFilter = (global: string) => {
		setPage(0);
		setGlobalFilter(global);
		filters.globalFilter = global;
		filters.pageNumber = 0;

		callRefresh();
	};

	const handleChangePage = (event: any, newPage: number) => {
		setPage(newPage);
		filters.pageNumber = newPage;
		callRefresh();
	};

	const handleChangeRowsPerPage = (event: any) => {
		const newPerPage = parseInt(event.target.value, 10);
		setRowsPerPage(newPerPage);
		setPage(0);
		filters.pageNumber = 0;
		filters.rowsPerPage = newPerPage;

		callRefresh();
	};

	const handleTicketDateFilterChange = (date: Date) => {
		setPage(0);
		setDateFilter(moment(date).startOf("day").toDate());
		filters.dateFilter = moment(date).startOf("day").toDate();
		filters.pageNumber = 0;
		callRefresh();
	};

	const handleTicketTypeFilterChange = (value: string[]) => {
		setPage(0);
		setTicketTypeFilter(value);
		filters.ticketTypeFilter = value;
		filters.pageNumber = 0;
		callRefresh();
	};

	const handleBuildingNameChange = (value: string) => {
		setPage(0);
		setBuildingFilter(value);
	};

	const rowsToDisplay = rows.filter((row) => {
		if (settings.ShowDateFilter && !props.UseDbFilterAndSort && dateFilter !== null && !dateFilter.toString().includes("Invalid") && dateFilter.getTime() !== new Date("1/1/1900").getTime() && dateFilter.getTime() !== new Date(new Date(row[settings.DateColumn]).toDateString()).getTime()) {
			return false;
		}

		if (settings.ShowGlobalFilter && !isNullOrEmpty(globalFilter) && !props.UseDbFilterAndSort) {
			var filter = globalFilter.toLowerCase();
			var includeRow = false;
			props.Configuration.filter((item) => {
				return item.visible;
			}).map((column) => {
				if (row.hasOwnProperty(column.columnName) && !isNullOrEmpty(row[column.columnName])) {
					includeRow = includeRow || row[column.columnName].toString().toLowerCase().includes(filter);
				}
			});

			if (!includeRow) {
				return false;
			}
		}

		if (props.Settings.UseTicketTypeFilter && !props.UseDbFilterAndSort && ticketTypeFilter != null) {
			return ticketTypeFilter.some((x) => {
				return row.ticket_types && row.ticket_types.indexOf(x) > -1;
			});
		}

		if (props.Settings.ShowBuildingsFilter && !isNullOrEmpty(buildingFilter)) {
			return row.building_name === buildingFilter;
		}

		return true;
	});

	return (
		<div className={classes.root}>
			<Paper className={classes.paper}>
				<EnhancedTableToolbar
					dateFilter={dateFilter}
					ShowBuildingsFilter={settings.ShowBuildingsFilter}
					ShowNewButton={settings.ShowNewButton}
					ShowGlobalFilter={settings.ShowGlobalFilter}
					variant={settings.Condensed ? "dense" : "regular"}
					FilterCallback={changeGlobalFilter}
					NewOnClick={settings.NewButtonAction}
					NewButtonText={settings.NewButtonText}
					Title={props.Title || "Tickets"}
					onChangeDateFilter={handleTicketDateFilterChange}
					showTicketTypeFilter={props.ShowTicketTypeFilter || false}
					showDateFilter={settings.ShowDateFilter}
					ticketTypeFilter={ticketTypeFilter}
					onChangeTicketTypeFilter={handleTicketTypeFilterChange}
					FilterBuildingName={handleBuildingNameChange}
				/>
				<div className={classes.tableWrapper}>
					<Table className={classes.table} aria-labelledby="tableTitle" size="small" aria-label="enhanced table">
						<EnhancedTableHead cells={props.Configuration} classes={classes} order={order as SortDirection} orderBy={orderBy} onRequestSort={handleRequestSort} rowCount={props.DataSet.length} />
						<TableBody>
							{(props.UseDbFilterAndSort ? rowsToDisplay : stableSort(rowsToDisplay, getSorting(order, orderBy))).slice(props.UseDbFilterAndSort ? 0 : page * rowsPerPage, props.UseDbFilterAndSort ? rowsPerPage : page * rowsPerPage + rowsPerPage).map((row, index) => {
								return (
									<TableRow hover={typeof props.OnRowClick === "function"} style={{ cursor: props.OnRowClick ? "pointer" : "auto" }} tabIndex={-1} key={index} onClick={() => props.OnRowClick && props.OnRowClick(row)}>
										{props.Configuration.filter((item, idx) => {
											return item.visible;
										}).map((item, idx) => {
											var data = null;
											if (item.typeCode !== "ACTION") {
												data = row[item.columnName];
											} else {
												data = item.header;
											}
											return (
												<TableCell key={idx} style={item.style}>
													{props.Modifiable && item.Modifiable && (
														<>
															{item.typeCode === "L" && <Checkbox style={{ padding: 0 }}></Checkbox>}
															{item.typeCode === "D" && <>test</>}
														</>
													)}
													{(!props.Modifiable || !item.Modifiable) && (item.typeCode !== "ACTION" || isNullOrUndefined(item.Condition) || item.Condition(row)) ? (
														Formatter(data, item.typeCode, {
															OnClick: item.OnClick
																? (event: any) => {
																		event.preventDefault();
																		event.stopPropagation();
																		item.OnClick(row);
																  }
																: null,
															OnChange: item.OnChange
																? (event: any) => {
																		event.preventDefault();
																		event.stopPropagation();
																		item.OnChange(row, event.target.value);
																  }
																: null,
														})
													) : (
														<></>
													)}
												</TableCell>
											);
										})}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</div>

				<TablePagination rowsPerPageOptions={!settings.Condensed ? [5, 10, 25, 50] : [10]} component="div" count={props.UseDbFilterAndSort && rowsToDisplay.length ? rowsToDisplay[0]["total_row_count"] : rowsToDisplay.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
			</Paper>
		</div>
	);
}

DynamicGrid.propTypes = {
	DataSet: PropTypes.array.isRequired,
	Configuration: PropTypes.array.isRequired,
	Settings: PropTypes.object,
};

export default DynamicGrid;
